(() => {
  'use strict';

  if (window.__life100EnhancementsLoaded) return;
  window.__life100EnhancementsLoaded = true;

  const SWATCH_LIMIT = 6;
  const CONTENT_PROTECTION_EDITABLE_SELECTOR = "input, textarea, select, option, [contenteditable='true'], [contenteditable='plaintext-only']";
  const normalizePolicyLabels = (root = document) => {
    if (!document.documentElement.lang.toLowerCase().startsWith('en')) return;

    root.querySelectorAll('.shopify-policy__title h1').forEach((heading) => {
      if (/^Refund Policy$/i.test(heading.textContent.trim())) heading.textContent = 'Refund & Return';
    });

    root.querySelectorAll('.section-footer a[href*="/policies/refund-policy"]').forEach((link) => {
      if (/^Return$/i.test(link.textContent.trim())) link.textContent = 'Refund & Return';
    });
  };

  const eventElement = (event) => {
    if (event.target instanceof Element) return event.target;
    return event.target?.parentElement || null;
  };

  const isEditableProtectionTarget = (event) => Boolean(eventElement(event)?.closest(CONTENT_PROTECTION_EDITABLE_SELECTOR));

  const enableContentProtection = () => {
    if (window.Shopify?.designMode) return;
    document.documentElement.classList.add('life100-content-protection');
    document.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));

    document.addEventListener('contextmenu', (event) => {
      if (!isEditableProtectionTarget(event)) event.preventDefault();
    }, { capture: true });

    document.addEventListener('dragstart', (event) => {
      if (eventElement(event)?.closest('img, picture, video')) event.preventDefault();
    }, { capture: true });

    document.addEventListener('selectstart', (event) => {
      if (!isEditableProtectionTarget(event)) event.preventDefault();
    }, { capture: true });
  };


  const setSwatchState = (container, expanded) => {
    const items = [...container.querySelectorAll(':scope > .product-block-options__item')];
    if (!items.length) return;

    const wrapper = container.closest('.product-block-options--swatch');
    if (!wrapper) return;

    let moreButton = container.querySelector(':scope > .product-block-options__more-label');
    const remaining = Math.max(0, items.length - SWATCH_LIMIT);

    const visibleItems = new Set(items.slice(0, SWATCH_LIMIT));
    const currentItem = items.find((item) => item.classList.contains('swatch-is-current') || item.getAttribute('aria-current') === 'true');
    if (currentItem && !visibleItems.has(currentItem) && items[SWATCH_LIMIT - 1]) {
      visibleItems.delete(items[SWATCH_LIMIT - 1]);
      visibleItems.add(currentItem);
    }

    items.forEach((item) => {
      item.classList.toggle('product-block-options__item--truncated', !visibleItems.has(item));
    });

    if (!remaining) {
      moreButton?.remove();
      wrapper.removeAttribute('data-swatch-expanded');
      return;
    }

    if (!moreButton || moreButton.tagName !== 'BUTTON') {
      const replacement = document.createElement('button');
      replacement.type = 'button';
      replacement.className = 'product-block-options__more-label';
      replacement.dataset.swatchMore = '';
      replacement.setAttribute('aria-expanded', 'false');
      if (moreButton) moreButton.replaceWith(replacement);
      else container.appendChild(replacement);
      moreButton = replacement;
    }

    wrapper.dataset.swatchExpanded = String(Boolean(expanded));
    moreButton.setAttribute('aria-expanded', String(Boolean(expanded)));
    moreButton.setAttribute('aria-label', expanded ? 'All colours shown' : `Show ${remaining} more colours`);
    const moreText = expanded ? '' : `+ ${remaining}`;
    if (moreButton.textContent !== moreText) moreButton.textContent = moreText;
  };

  const markCurrentSwatch = (card) => {
    const swatches = [...card.querySelectorAll('.product-block-options__item[data-option-item]')];
    if (!swatches.length) return;

    let current = null;
    const activeImage = card.querySelector('.product-block__image--active[data-media-id]');
    if (activeImage?.dataset.mediaId) {
      current = swatches.find((swatch) => swatch.dataset.media === activeImage.dataset.mediaId) || null;
    }

    if (!current) {
      const productLink = card.querySelector('a[href*="/products/"][href*="Color="]');
      if (productLink) {
        try {
          const color = new URL(productLink.getAttribute('href'), window.location.origin).searchParams.get('Color');
          current = swatches.find((swatch) => normalizeColor(swatch.dataset.optionItem) === normalizeColor(color)) || null;
        } catch (error) {
          current = null;
        }
      }
    }

    if (!current) return;
    swatches.forEach((swatch) => {
      const selected = swatch === current;
      swatch.classList.toggle('swatch-is-current', selected);
      if (selected) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });
  };

  const applySwatchLimits = (root = document) => {
    root.querySelectorAll('.product-block').forEach(markCurrentSwatch);
    root.querySelectorAll('.product-block:not(.product-block--compact) .product-block-options--swatch .product-block-options__inner').forEach((container) => {
      const expanded = container.closest('.product-block-options--swatch')?.dataset.swatchExpanded === 'true';
      setSwatchState(container, expanded);
    });
  };

  let scanQueued = false;
  const queueEnhancementScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      applySwatchLimits();
    });
  };

  const mutationNeedsScan = (mutation) => {
    if (mutation.type === 'attributes') return mutation.attributeName === 'href' && Boolean(mutation.target.closest?.('.product-block'));
    return mutation.target.closest?.('.product-block-options__inner') || [...mutation.addedNodes].some((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      return node.matches?.('.product-block, .product-block-options__inner')
        || node.querySelector?.('.product-block, .product-block-options__inner');
    });
  };

  const updateMediaDots = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;
    gallery.querySelectorAll('[data-life100-media-dot]').forEach((dot) => {
      const active = dot.dataset.life100MediaDot === String(mediaId);
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });
  };

  document.addEventListener('click', (event) => {

    const more = event.target.closest('[data-swatch-more]');
    if (more) {
      event.preventDefault();
      event.stopPropagation();
      const container = more.closest('.product-block-options__inner');
      const wrapper = container?.closest('.product-block-options--swatch');
      if (container && wrapper) setSwatchState(container, wrapper.dataset.swatchExpanded !== 'true');
      return;
    }

    if (event.target.closest('.product-block-options__item[data-option-item]')) {
      queueEnhancementScan();
    }

    const mediaDot = event.target.closest('[data-life100-media-dot]');
    if (mediaDot) {
      const gallery = mediaDot.closest('media-gallery');
      const carousel = gallery?.querySelector('.main-image > carousel-slider');
      const slide = carousel?.querySelector(`.slider__item[data-media-id="${CSS.escape(mediaDot.dataset.life100MediaDot)}"]`);
      if (carousel && slide) {
        carousel.scrollToElement?.(slide, 'smooth');
        updateMediaDots(gallery, mediaDot.dataset.life100MediaDot);
      }
      return;
    }

    const dialogOpen = event.target.closest('[data-life100-dialog-open]');
    if (dialogOpen) {
      document.getElementById(dialogOpen.dataset.life100DialogOpen)?.showModal?.();
      return;
    }

    const dialogClose = event.target.closest('[data-life100-dialog-close]');
    if (dialogClose) {
      dialogClose.closest('dialog')?.close();
      return;
    }

  });

  document.addEventListener('on:cart:add', (event) => {
    event.target.closest?.('.life100-variant-dialog')?.close();
  });

  document.addEventListener('on:carousel-slider:select', (event) => {
    const gallery = event.target.closest?.('media-gallery');
    const mediaId = event.detail?.slide?.dataset?.mediaId;
    if (gallery && mediaId) updateMediaDots(gallery, mediaId);
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-picker')) queueEnhancementScan();
  });

  const initialize = () => {
    enableContentProtection();
    normalizePolicyLabels();
    applySwatchLimits();

    const observer = new MutationObserver((mutations) => {
      if (mutations.some(mutationNeedsScan)) queueEnhancementScan();
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });

    document.addEventListener('shopify:section:load', (event) => {
      normalizePolicyLabels(event.target || document);
      queueEnhancementScan();
    });

    window.setTimeout(queueEnhancementScan, 500);
    window.setTimeout(queueEnhancementScan, 1500);
    window.setTimeout(queueEnhancementScan, 3000);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
